import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import Button from '../components/ui/button';
import { toast } from 'sonner';
import {
  getTicketVolumeTrends,
  getResponseTimeAnalytics,
  getResolutionTimeReports,
  getAgentPerformanceMetrics,
  getCompanyWiseAnalysis,
  getTotalTicketsReport
} from '../api/reports';
import {
  BarChart3,
  TrendingUp,
  Clock,
  Users,
  Download,
  Calendar,
  Activity,
  ChevronDown,
  RefreshCw
} from 'lucide-react';

// Safe default structures so UI never breaks when one API fails
const DEFAULT_RESPONSE_STATS = {
  totalTickets: 0,
  avgResponseHours: 0,
  minResponseHours: 0,
  maxResponseHours: 0,
  byRange: { '0-1h': 0, '1-4h': 0, '4-24h': 0, '24h+': 0 }
};
const DEFAULT_RESOLUTION_STATS = {
  totalResolved: 0,
  avgResolutionHours: 0,
  avgResolutionDays: 0,
  byRange: { '0-1d': 0, '1-3d': 0, '3-7d': 0, '7d+': 0 },
  byPriority: []
};

const toLocalDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [period, setPeriod] = useState('monthly');
  const [dateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // Data states – always use safe defaults so UI never crashes
  const [volumeTrends, setVolumeTrends] = useState([]);
  const [totalTicketsFromApi, setTotalTicketsFromApi] = useState(null);
  const [responseTimes, setResponseTimes] = useState({ responseTimes: [], statistics: DEFAULT_RESPONSE_STATS });
  const [resolutionTimes, setResolutionTimes] = useState({ resolutionTimes: [], statistics: DEFAULT_RESOLUTION_STATS });
  const [agentMetrics, setAgentMetrics] = useState([]);
  const [companyAnalysis, setCompanyAnalysis] = useState([]);

  const fetchReportsData = useCallback(async () => {
    try {
      setLoading(true);
      setFetchError(null);
      const now = new Date();
      const todayIso = toLocalDate(now);
      const defaultStartForDaily = toLocalDate(new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000));
      const useDefaultDailyWindow = period === 'daily' && !dateRange.startDate && !dateRange.endDate;
      const params = {
        period,
        ...(useDefaultDailyWindow
          ? { startDate: `${defaultStartForDaily} 00:00:00`, endDate: `${todayIso} 23:59:59` }
          : {}),
        ...(!useDefaultDailyWindow && dateRange.startDate && { startDate: `${dateRange.startDate} 00:00:00` }),
        ...(!useDefaultDailyWindow && dateRange.endDate && { endDate: `${dateRange.endDate} 23:59:59` })
      };

      const [trendsRes, responseRes, resolutionRes, agentRes, companyRes] = await Promise.all([
        getTicketVolumeTrends(params).catch(err => {
          console.error('Volume trends error:', err);
          return { success: false, data: { trends: [] } };
        }),
        getResponseTimeAnalytics(params).catch(err => {
          console.error('Response times error:', err);
          return { success: false, data: { responseTimes: [], statistics: DEFAULT_RESPONSE_STATS } };
        }),
        getResolutionTimeReports(params).catch(err => {
          console.error('Resolution times error:', err);
          return { success: false, data: { resolutionTimes: [], statistics: DEFAULT_RESOLUTION_STATS } };
        }),
        user?.role === 'admin' || user?.role === 'employee' 
          ? getAgentPerformanceMetrics(params).catch(err => {
              console.error('Agent metrics error:', err);
              console.error('Agent metrics error details:', err.response?.data || err.message);
              return { success: false, data: { agentMetrics: [] } };
            })
          : Promise.resolve({ success: true, data: { agentMetrics: [] } }),
        getCompanyWiseAnalysis(params).catch(err => {
          console.error('Company analysis error:', err);
          console.error('Company analysis error details:', err.response?.data || err.message);
          return { success: false, data: { companyAnalysis: [] } };
        })
      ]);

      // Debug logging (dev only – avoid noise in production)
      if (process.env.NODE_ENV === 'development') {
        console.log('Reports API Responses:', {
          trendsRes: trendsRes?.success ? 'Success' : 'Failed',
          responseRes: responseRes?.success ? 'Success' : 'Failed',
          resolutionRes: resolutionRes?.success ? 'Success' : 'Failed',
          agentRes: agentRes?.success ? 'Success' : 'Failed',
          companyRes: companyRes?.success ? 'Success' : 'Failed'
        });
      }

      // Set data with defensive handling (production: accept data when present even if success is missing)
      const hasTrends = trendsRes?.data != null && trendsRes.success !== false;
      const trendsList = hasTrends && Array.isArray(trendsRes.data.trends) ? trendsRes.data.trends : [];
      const totalFromVolume = hasTrends && typeof trendsRes.data.totalTickets === 'number' ? trendsRes.data.totalTickets : null;
      setVolumeTrends(trendsList);
      setTotalTicketsFromApi(totalFromVolume);

      // When volume-trends fails or doesn't return totalTickets, fetch total from fallback so Total Tickets + chart still work
      if (totalFromVolume == null) {
        getTotalTicketsReport()
          .then((res) => {
            const total = res?.data?.totalTickets;
            if (typeof total === 'number') {
              setTotalTicketsFromApi(total);
              if (trendsList.length === 0 && total > 0) {
                setVolumeTrends([
                  { period: 'all', label: 'All time', count: total, open_count: 0, in_progress_count: 0, resolved_count: 0, closed_count: 0 }
                ]);
              }
            }
          })
          .catch(() => {});
      }

      const hasResponse = responseRes?.data != null && responseRes.success !== false;
      setResponseTimes({
        responseTimes: hasResponse && Array.isArray(responseRes.data.responseTimes) ? responseRes.data.responseTimes : [],
        statistics: hasResponse && responseRes.data.statistics && typeof responseRes.data.statistics === 'object'
          ? { ...DEFAULT_RESPONSE_STATS, ...responseRes.data.statistics }
          : DEFAULT_RESPONSE_STATS
      });

      const hasResolution = resolutionRes?.data != null && resolutionRes.success !== false;
      setResolutionTimes({
        resolutionTimes: hasResolution && Array.isArray(resolutionRes.data.resolutionTimes) ? resolutionRes.data.resolutionTimes : [],
        statistics: hasResolution && resolutionRes.data.statistics && typeof resolutionRes.data.statistics === 'object'
          ? { ...DEFAULT_RESOLUTION_STATS, ...resolutionRes.data.statistics }
          : DEFAULT_RESOLUTION_STATS
      });

      const hasAgents = agentRes?.data != null && agentRes.success !== false;
      setAgentMetrics(hasAgents && Array.isArray(agentRes.data.agentMetrics) ? agentRes.data.agentMetrics : []);

      const hasCompany = companyRes?.data != null && companyRes.success !== false;
      setCompanyAnalysis(hasCompany && Array.isArray(companyRes.data.companyAnalysis) ? companyRes.data.companyAnalysis : []);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setFetchError(error?.message || 'Failed to load reports');
      toast.error('Failed to load reports. Use Refresh to try again.');
      setVolumeTrends([]);
      setTotalTicketsFromApi(null);
      setResponseTimes({ responseTimes: [], statistics: DEFAULT_RESPONSE_STATS });
      setResolutionTimes({ resolutionTimes: [], statistics: DEFAULT_RESOLUTION_STATS });
      setAgentMetrics([]);
      setCompanyAnalysis([]);
    } finally {
      setLoading(false);
    }
  }, [period, dateRange, user]);

  useEffect(() => {
    if (user) {
      fetchReportsData();
    }
  }, [fetchReportsData, user]);

  const exportToPDF = () => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error('Please allow pop-ups to export PDF');
        return;
      }

      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Reports Export - ${new Date().toLocaleDateString()}</title>
            <style>
              @media print {
                @page { margin: 1cm; size: A4 landscape; }
              }
              body {
                font-family: Arial, sans-serif;
                font-size: 12px;
                margin: 20px;
              }
              h1 { font-size: 24px; margin-bottom: 20px; }
              h2 { font-size: 18px; margin-top: 30px; margin-bottom: 10px; }
              table { width: 100%; border-collapse: collapse; margin-top: 10px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f0f0f0; font-weight: bold; }
              .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
              .summary-card { border: 1px solid #ddd; padding: 15px; border-radius: 5px; }
              .summary-card h3 { margin: 0 0 10px 0; font-size: 14px; color: #666; }
              .summary-card p { margin: 0; font-size: 24px; font-weight: bold; }
            </style>
          </head>
          <body>
            <h1>Reports & Analytics Export</h1>
            <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
            <p><strong>Period:</strong> ${period}</p>
            ${dateRange.startDate ? `<p><strong>Date Range:</strong> ${dateRange.startDate} to ${dateRange.endDate || 'Present'}</p>` : ''}
            
            <div class="summary">
              <div class="summary-card">
                <h3>Total Tickets</h3>
                <p>${formatNumber(totalTicketsFromApi != null ? totalTicketsFromApi : (Array.isArray(volumeTrends) ? volumeTrends.reduce((sum, t) => sum + Number(t.count || 0), 0) : 0))}</p>
              </div>
              <div class="summary-card">
                <h3>Avg Business Response Time</h3>
                <p>${responseTimes?.statistics != null && typeof responseTimes.statistics.avgResponseHours === 'number' && (responseTimes.statistics.totalTickets ?? 0) > 0 ? formatHours(responseTimes.statistics.avgResponseHours) : 'N/A'}</p>
              </div>
              <div class="summary-card">
                <h3>Avg Business Resolution Time</h3>
                <p>${resolutionTimes?.statistics != null && typeof resolutionTimes.statistics.avgResolutionDays === 'number' ? `${resolutionTimes.statistics.avgResolutionDays.toFixed(1)}d` : 'N/A'}</p>
              </div>
              <div class="summary-card">
                <h3>Total Companies</h3>
                <p>${formatNumber(Array.isArray(companyAnalysis) ? companyAnalysis.length : 0)}</p>
              </div>
            </div>

            ${Array.isArray(volumeTrends) && volumeTrends.length > 0 ? `
              <h2>Ticket Volume Trends</h2>
              <table>
                <thead>
                  <tr>
                    <th>Period</th>
                    <th>Total</th>
                    <th>Open</th>
                    <th>In Progress</th>
                    <th>Resolved</th>
                    <th>Closed</th>
                  </tr>
                </thead>
                <tbody>
                  ${volumeTrends.map(t => `
                    <tr>
                      <td>${t.label || t.period}</td>
                      <td>${t.count || 0}</td>
                      <td>${t.open_count || 0}</td>
                      <td>${t.in_progress_count || 0}</td>
                      <td>${t.resolved_count || 0}</td>
                      <td>${t.closed_count || 0}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}

            ${agentMetrics.length > 0 ? `
              <h2>Agent Performance</h2>
              <table>
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Total Tickets</th>
                    <th>Open</th>
                    <th>In Progress</th>
                    <th>Resolved</th>
                    <th>Closed</th>
                    <th>Avg Resolution (Days)</th>
                  </tr>
                </thead>
                <tbody>
                  ${agentMetrics.map(a => `
                    <tr>
                      <td>${a.name}</td>
                      <td>${a.total_tickets || 0}</td>
                      <td>${a.open_tickets || 0}</td>
                      <td>${a.in_progress_tickets || 0}</td>
                      <td>${a.resolved_tickets || 0}</td>
                      <td>${a.closed_tickets || 0}</td>
                      <td>${a.avg_resolution_hours ? (a.avg_resolution_hours / 24).toFixed(1) : 'N/A'}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}

            ${companyAnalysis.length > 0 ? `
              <h2>Company Analysis</h2>
              <table>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Total Tickets</th>
                    <th>Open</th>
                    <th>In Progress</th>
                    <th>Resolved</th>
                    <th>Closed</th>
                    <th>Avg Resolution (Days)</th>
                    <th>Customers</th>
                  </tr>
                </thead>
                <tbody>
                  ${companyAnalysis.map(c => `
                    <tr>
                      <td>${c.company_name}</td>
                      <td>${c.total_tickets || 0}</td>
                      <td>${c.open_tickets || 0}</td>
                      <td>${c.in_progress_tickets || 0}</td>
                      <td>${c.resolved_tickets || 0}</td>
                      <td>${c.closed_tickets || 0}</td>
                      <td>${c.avg_resolution_hours ? (c.avg_resolution_hours / 24).toFixed(1) : 'N/A'}</td>
                      <td>${c.unique_customers || 0}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            ` : ''}
          </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          toast.success('PDF export ready. Use your browser\'s print dialog to save as PDF.');
        }, 250);
      };
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  const exportToExcel = () => {
    try {
      // Create CSV content
      const csvRows = [];
      
      // Add summary
      csvRows.push(['Reports & Analytics Export']);
      csvRows.push(['Generated:', new Date().toLocaleString()]);
      csvRows.push(['Period:', period]);
      if (dateRange.startDate) {
        csvRows.push(['Date Range:', `${dateRange.startDate} to ${dateRange.endDate || 'Present'}`]);
      }
      csvRows.push([]);

      // Volume trends
      if (Array.isArray(volumeTrends) && volumeTrends.length > 0) {
        csvRows.push(['Ticket Volume Trends']);
        csvRows.push(['Period', 'Total', 'Open', 'In Progress', 'Resolved', 'Closed']);
        volumeTrends.forEach(t => {
          csvRows.push([
            t.label || t.period,
            t.count || 0,
            t.open_count || 0,
            t.in_progress_count || 0,
            t.resolved_count || 0,
            t.closed_count || 0
          ]);
        });
        csvRows.push([]);
      }

      // Agent performance
      if (agentMetrics.length > 0) {
        csvRows.push(['Agent Performance']);
        csvRows.push(['Agent', 'Total Tickets', 'Open', 'In Progress', 'Resolved', 'Closed', 'Avg Resolution (Days)']);
        agentMetrics.forEach(a => {
          csvRows.push([
            a.name,
            a.total_tickets || 0,
            a.open_tickets || 0,
            a.in_progress_tickets || 0,
            a.resolved_tickets || 0,
            a.closed_tickets || 0,
            a.avg_resolution_hours ? (a.avg_resolution_hours / 24).toFixed(1) : 'N/A'
          ]);
        });
        csvRows.push([]);
      }


      
      // Company analysis
      if (companyAnalysis.length > 0) {
        csvRows.push(['Company Analysis']);
        csvRows.push(['Company', 'Total Tickets', 'Open', 'In Progress', 'Resolved', 'Closed', 'Avg Resolution (Days)', 'Customers']);
        companyAnalysis.forEach(c => {
          csvRows.push([
            c.company_name,
            c.total_tickets || 0,
            c.open_tickets || 0,
            c.in_progress_tickets || 0,
            c.resolved_tickets || 0,
            c.closed_tickets || 0,
            c.avg_resolution_hours ? (c.avg_resolution_hours / 24).toFixed(1) : 'N/A',
            c.unique_customers || 0
          ]);
        });
      }

      // Convert to CSV string
      const csvContent = csvRows.map(row => 
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ).join('\n');

      // Add BOM for UTF-8
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `reports_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Reports exported to CSV successfully');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    }
  };

  // Format a duration given in hours into a human-friendly string,
  // e.g. 0.5 -> "30m", 3.33 -> "3h 20m", 27 -> "1d 12h"
  // Uses real numeric values from the API (not hardcoded). Sub-hour averages use minutes.
  const formatHours = (hours, emptyLabel = 'N/A') => {
    const h = Number(hours);
    if (!Number.isFinite(h) || h < 0) return emptyLabel;
    if (h === 0) return emptyLabel;

    // Avoid rounding sub-minute positives to 0m (was showing misleading "0h")
    const rawMinutes = h * 60;
    const totalMinutes = rawMinutes < 1 && rawMinutes > 0
      ? Math.max(1, Math.ceil(rawMinutes - 1e-9))
      : Math.round(rawMinutes);
    const minutesInDay = 24 * 60;

    const days = Math.floor(totalMinutes / minutesInDay);
    let remainingMinutes = totalMinutes - days * minutesInDay;
    const wholeHours = Math.floor(remainingMinutes / 60);
    remainingMinutes -= wholeHours * 60;

    const parts = [];

    if (days > 0) {
      parts.push(`${days}d`);
      if (wholeHours > 0) parts.push(`${wholeHours}h`);
    } else {
      if (wholeHours > 0) parts.push(`${wholeHours}h`);
      if (remainingMinutes > 0 && parts.length < 2) {
        parts.push(`${remainingMinutes}m`);
      }
    }

    if (parts.length === 0) {
      // e.g. <1 min total but h > 0
      return `${rawMinutes.toFixed(1)}m`;
    }
    return parts.join(' ');
  };

  const formatNumber = (num) => {
    const n = Number(num);
    if (num == null || num === '' || !Number.isFinite(n) || n < 0) return '0';
    return n.toLocaleString();
  };

  return (
    <DashboardLayout userRole={user?.role || 'customer'}>
      <div className="min-h-full">
        {/* Header – stacked on mobile, row on desktop */}
        <div className="border-b border-gray-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl px-4 sm:px-6 py-4 sm:py-5 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Reports & Analytics</h1>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">Comprehensive insights into your ticket data</p>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3">
              <div className="relative group w-full sm:w-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                  <Calendar className="h-4 w-4 text-indigo-500 dark:text-indigo-400" />
                </div>
                <select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  className="w-full sm:w-auto appearance-none pl-10 pr-10 py-2.5 bg-white/90 dark:bg-slate-800/90 border border-gray-200 dark:border-slate-700 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-200 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 transition-all cursor-pointer"
                >
                  <option value="daily">Daily View</option>
                  <option value="weekly">Weekly View</option>
                  <option value="monthly">Monthly View</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  onClick={() => fetchReportsData()}
                  variant="unstyled"
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold rounded-xl border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 flex-1 sm:flex-none"
                  disabled={loading}
                >
                  <RefreshCw className={`w-4 h-4 shrink-0 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button
                  onClick={exportToPDF}
                  variant="danger"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap flex-1 sm:flex-none"
                >
                  <Download className="w-4 h-4 shrink-0" />
                  Export PDF
                </Button>
                <Button
                  onClick={exportToExcel}
                  variant="unstyled"
                  className="inline-flex items-center justify-center gap-2 h-10 px-4 text-sm font-semibold rounded-xl bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white shadow-md flex-1 sm:flex-none"
                >
                  <Download className="w-4 h-4 shrink-0" />
                  Export Excel
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs – horizontally scrollable on mobile, scrollbar hidden */}
        <div className="border-b border-gray-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md px-4 sm:px-6 relative z-10">
          <div className="flex items-center gap-1 overflow-x-auto overflow-y-hidden scrollbar-hide -mx-1 px-1 pb-px" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'volume', label: 'Volume Trends', icon: TrendingUp },
              { id: 'response', label: 'Response Times', icon: Clock },
              { id: 'resolution', label: 'Resolution Times', icon: Activity },
              ...(user?.role === 'admin' || user?.role === 'employee' ? [{ id: 'agents', label: 'Agent Performance', icon: Users }] : [])
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3 text-sm font-semibold rounded-t-lg transition-all duration-200 flex items-center gap-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-sm border border-b-0 border-gray-200 dark:border-slate-700'
                    : 'text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-50 dark:hover:bg-slate-800/50'
                }`}
              >
                <tab.icon className="w-4 h-4 shrink-0" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-6 sm:py-8 relative z-10 bg-gradient-to-br from-gray-50 via-gray-50/70 to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/30 min-h-screen">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-400 font-medium">Loading reports...</span>
            </div>
          ) : fetchError ? (
            <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-8 sm:p-12 shadow-xl text-center">
              <p className="text-gray-600 dark:text-gray-400 font-medium mb-2">Reports could not be loaded</p>
              <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">{fetchError}</p>
              <Button onClick={() => fetchReportsData()} variant="unstyled" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700">
                <RefreshCw className="w-4 h-4" />
                Try again
              </Button>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="space-y-6">
                  {/* Summary Cards – responsive grid, glassmorphism */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-5 sm:p-6 shadow-lg shadow-gray-200/50 dark:shadow-black/20 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Tickets</p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                            {formatNumber(
                              totalTicketsFromApi != null
                                ? totalTicketsFromApi
                                : (Array.isArray(volumeTrends) ? volumeTrends.reduce((sum, t) => sum + Number(t.count || 0), 0) : 0)
                            )}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">All time tickets</p>
                        </div>
                        <div className="p-3 rounded-xl bg-blue-100/80 dark:bg-blue-900/30 shrink-0">
                          <BarChart3 className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-5 sm:p-6 shadow-lg shadow-gray-200/50 dark:shadow-black/20 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Avg Business Response Time</p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                            {responseTimes?.statistics != null &&
                            typeof responseTimes.statistics.avgResponseHours === 'number' &&
                            (responseTimes.statistics.totalTickets ?? 0) > 0
                              ? formatHours(responseTimes.statistics.avgResponseHours)
                              : 'N/A'}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">First staff reply (wall-clock), capped at 2h for chart scale</p>
                        </div>
                        <div className="p-3 rounded-xl bg-emerald-100/80 dark:bg-emerald-900/30 shrink-0">
                          <Clock className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-5 sm:p-6 shadow-lg shadow-gray-200/50 dark:shadow-black/20 hover:shadow-xl transition-all duration-300">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Avg Business Resolution Time</p>
                          <p className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                            {resolutionTimes?.statistics != null &&
                            typeof resolutionTimes.statistics.avgResolutionHours === 'number' &&
                            (resolutionTimes.statistics.totalResolved ?? 0) > 0
                              ? formatHours(resolutionTimes.statistics.avgResolutionHours)
                              : 'N/A'}
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {(resolutionTimes?.statistics != null && typeof resolutionTimes.statistics.avgResolutionHours === 'number' && (resolutionTimes.statistics.totalResolved ?? 0) > 0)
                              ? 'Resolved/closed tickets (wall-clock; capped at 2d)'
                              : 'N/A when no resolved/closed tickets with closure date'}
                          </p>
                        </div>
                        <div className="p-3 rounded-xl bg-purple-100/80 dark:bg-purple-900/30 shrink-0">
                          <Activity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Volume Trends Chart – glassmorphism, scrollable on mobile */}
                  <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-4 sm:p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" aria-hidden="true" />
                    <h3 className="relative text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 flex items-center gap-2">
                      <div className="w-1 h-5 bg-indigo-500 rounded-full" />
                      Ticket Volume Trends
                    </h3>
                    <VolumeTrendsChart
                      data={
                        Array.isArray(volumeTrends) && volumeTrends.length > 0
                          ? volumeTrends
                          : totalTicketsFromApi != null && totalTicketsFromApi > 0
                            ? [{ period: 'all', label: 'All time', count: totalTicketsFromApi, open_count: 0, in_progress_count: 0, resolved_count: 0, closed_count: 0 }]
                            : []
                      }
                      period={period}
                    />
                  </div>

                  {/* Response Time Distribution – always render with safe byRange (chart handles empty) */}
                  {responseTimes?.statistics?.byRange && typeof responseTimes.statistics.byRange === 'object' && (
                    <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-800/50 p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                      <h3 className="relative text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                        <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                        Response Time Distribution
                      </h3>
                      <ResponseTimeChart data={responseTimes.statistics.byRange || {}} />
                    </div>
                  )}
                </div>
              )}

              {/* Volume Trends Tab */}
              {activeTab === 'volume' && (
                <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-4 sm:p-6 shadow-xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" aria-hidden="true" />
                  <h3 className="relative text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-indigo-500 rounded-full" />
                    Ticket Volume Trends
                  </h3>
                  <VolumeTrendsChart
                    data={
                      Array.isArray(volumeTrends) && volumeTrends.length > 0
                        ? volumeTrends
                        : totalTicketsFromApi != null && totalTicketsFromApi > 0
                          ? [{ period: 'all', label: 'All time', count: totalTicketsFromApi, open_count: 0, in_progress_count: 0, resolved_count: 0, closed_count: 0 }]
                          : []
                    }
                    detailed
                    period={period}
                  />
                </div>
              )}

              {/* Response Times Tab – statistics always defined (safe defaults) */}
              {activeTab === 'response' && (
                <div className="space-y-6">
                  {responseTimes?.statistics && (
                    <>
                      <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-800/50 p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                        <h3 className="relative text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                          <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                          Business Response Time Statistics
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div className="text-center p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Average</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                              {(responseTimes.statistics.totalTickets ?? 0) > 0
                                ? formatHours(responseTimes.statistics.avgResponseHours)
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="text-center p-5 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Min</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-2">
                              {(responseTimes.statistics.totalTickets ?? 0) > 0
                                ? formatHours(responseTimes.statistics.minResponseHours)
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="text-center p-5 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Max</p>
                            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400 mt-2">
                              {(responseTimes.statistics.totalTickets ?? 0) > 0
                                ? formatHours(responseTimes.statistics.maxResponseHours)
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="text-center p-5 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Tickets</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                              {formatNumber(responseTimes.statistics.totalTickets ?? 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                      {responseTimes.statistics.byRange && typeof responseTimes.statistics.byRange === 'object' && Object.keys(responseTimes.statistics.byRange).length > 0 ? (
                        <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-800/50 p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                          <h3 className="relative text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                            <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                            Response Time Distribution
                          </h3>
                          <ResponseTimeChart data={responseTimes.statistics.byRange || {}} />
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 p-6">
                          <div className="flex flex-col items-center justify-center py-12 text-gray-400 dark:text-gray-500">
                            <Clock className="w-10 h-10 mx-auto mb-2 opacity-50" />
                            <p className="text-sm font-medium">No response time distribution yet</p>
                            <p className="text-xs mt-1">Replies by staff will show here</p>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Resolution Times Tab – statistics always defined (safe defaults) */}
              {activeTab === 'resolution' && (
                <div className="space-y-6">
                  {resolutionTimes?.statistics ? (
                    <>
                      <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-800/50 p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                        <h3 className="relative text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                          <div className="w-1 h-5 bg-purple-500 rounded-full"></div>
                          Business Resolution Time Statistics
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="text-center p-5 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Average Business Hours</p>
                            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                              {typeof resolutionTimes.statistics.avgResolutionHours === 'number' && (resolutionTimes.statistics.totalResolved ?? 0) > 0
                                ? formatHours(resolutionTimes.statistics.avgResolutionHours)
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="text-center p-5 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Average Days</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400 mt-2">
                              {typeof resolutionTimes.statistics.avgResolutionDays === 'number' && (resolutionTimes.statistics.totalResolved ?? 0) > 0
                                ? `${Number(resolutionTimes.statistics.avgResolutionDays).toFixed(1)}d`
                                : 'N/A'}
                            </p>
                          </div>
                          <div className="text-center p-5 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Resolved</p>
                            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                              {formatNumber(resolutionTimes.statistics.totalResolved ?? 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                      {resolutionTimes.statistics.byRange && typeof resolutionTimes.statistics.byRange === 'object' && Object.keys(resolutionTimes.statistics.byRange).length > 0 ? (
                        <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-800/50 p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                          {/* Decorative glow blob */}
                          <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                          <h3 className="relative text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                            <div className="w-1 h-5 bg-purple-500 rounded-full"></div>
                            Resolution Time Distribution
                          </h3>
                          <ResolutionTimeChart data={resolutionTimes.statistics.byRange || {}} />
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-gray-100/50 dark:border-slate-700 bg-gradient-to-br from-white to-gray-50 dark:from-slate-800 dark:to-slate-800/50 p-6 shadow-xl shadow-gray-200/40 dark:shadow-black/20 relative overflow-hidden">
                          <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                            <div className="w-1 h-5 bg-purple-500 rounded-full"></div>
                            Resolution Time Distribution
                          </h3>
                          <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
                            <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p className="text-base font-medium">No resolution time distribution data available</p>
                            <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">Data will appear when tickets are resolved</p>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-6 shadow-xl relative overflow-hidden">
                      <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
                        <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-base font-medium">No resolution time data available for this period</p>
                        <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">Data appears when tickets are resolved or closed with a resolution date set</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Agent Performance Tab */}
              {activeTab === 'agents' && (user?.role === 'admin' || user?.role === 'employee') && (
                <div className="rounded-2xl border border-gray-200/60 dark:border-slate-700/60 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm p-4 sm:p-6 shadow-xl relative overflow-hidden">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 sm:mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-indigo-500 rounded-full" />
                    Agent Performance Metrics
                  </h3>
                  <AgentPerformanceTable data={agentMetrics} />
                </div>
              )}

            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

// Volume Trends Chart – modern pill bars with hover tooltip
const VolumeTrendsChart = ({ data = [], detailed = false, period = 'monthly' }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const marginLeft = 40;
  const marginBottom = 44;
  const marginTop = 30;
  const chartHeight = detailed ? 400 : 340;
  const barAreaHeight = chartHeight - marginTop - marginBottom;
  const zeroY = chartHeight - marginBottom;

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-400 dark:text-slate-500 bg-slate-50/30 dark:bg-slate-900/30 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800">
        <TrendingUp className="w-12 h-12 mb-4 opacity-20" />
        <p className="text-lg font-semibold">No data for this period</p>
        <p className="text-sm opacity-60">Trends will appear once tickets are generated</p>
      </div>
    );
  }

  const maxCount = Math.max(...data.map((d) => d.count || 0), 5);
  const chartWidth = Math.max(data.length * 70, 800);
  const slotWidth = (chartWidth - marginLeft - 40) / data.length;
  const barWidth = Math.min(slotWidth * 0.6, 40);

  const yTicks = [0, Math.ceil(maxCount / 2), maxCount];
  const yToPixel = (value) => zeroY - (value / maxCount) * barAreaHeight;
  const formatXAxisLabel = (item) => {
    const rawPeriod = String(item?.period || '');
    if (period === 'daily') {
      const m = rawPeriod.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) return `${m[3]}/${m[2]}`;
    }
    if (period === 'weekly') {
      const w = rawPeriod.match(/^(\d{4})-(\d{2})$/);
      if (w) return `W${w[2]}`;
    }
    return item?.label || item?.period || '';
  };

  return (
    <div className="w-full overflow-x-auto scrollbar-hide">
      <div style={{ width: chartWidth, height: chartHeight }} className="relative">
        <svg width={chartWidth} height={chartHeight} className="overflow-visible">
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#818cf8" />
            </linearGradient>
            <filter id="glow">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {yTicks.map((tick) => (
            <g key={tick}>
              <line
                x1={marginLeft}
                y1={yToPixel(tick)}
                x2={chartWidth}
                y2={yToPixel(tick)}
                stroke="currentColor"
                className="text-slate-200 dark:text-slate-800"
                strokeDasharray="4 4"
              />
              <text
                x={marginLeft - 10}
                y={yToPixel(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                className="text-[11px] font-bold fill-slate-400"
              >
                {tick}
              </text>
            </g>
          ))}

          {data.map((item, i) => {
            const count = item.count || 0;
            const h = (count / maxCount) * barAreaHeight;
            const x = marginLeft + 20 + i * slotWidth + (slotWidth - barWidth) / 2;
            const y = zeroY - h;
            const isHovered = hoveredIndex === i;

            return (
              <g
                key={String(item.period || item.label || i)}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
                className="transition-all duration-300"
              >
                <rect x={x - 10} y={marginTop} width={barWidth + 20} height={barAreaHeight} fill="transparent" className="cursor-pointer" />

                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={Math.max(h, 4)}
                  rx={barWidth / 2}
                  fill="url(#barGradient)"
                  filter={isHovered ? 'url(#glow)' : 'none'}
                  className={`transition-all duration-500 ease-out ${isHovered ? 'opacity-100' : 'opacity-80'}`}
                />

                <text
                  x={x + barWidth / 2}
                  y={y - 10}
                  textAnchor="middle"
                  className={`text-[12px] font-black transition-opacity duration-300 ${isHovered ? 'opacity-100 fill-indigo-600' : 'opacity-0'}`}
                >
                  {count}
                </text>

                <text
                  x={x + barWidth / 2}
                  y={zeroY + 24}
                  textAnchor="middle"
                  className={`text-[11px] font-bold transition-colors ${isHovered ? 'fill-indigo-500' : 'fill-slate-400'}`}
                >
                  {formatXAxisLabel(item)}
                </text>
              </g>
            );
          })}
        </svg>

        {hoveredIndex !== null && data[hoveredIndex] && (
          <div
            className="absolute pointer-events-none bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-700 rounded-lg p-2 text-[10px] z-50"
            style={{
              left: marginLeft + 20 + hoveredIndex * slotWidth,
              top: yToPixel(data[hoveredIndex].count || 0) - 50
            }}
          >
            <div className="font-bold text-slate-900 dark:text-white mb-1 border-b border-slate-200 dark:border-slate-700 pb-1">
              {formatXAxisLabel(data[hoveredIndex])}
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-emerald-500">Resolved: {data[hoveredIndex].resolved_count || 0}</span>
              <span className="text-amber-500">Open: {data[hoveredIndex].open_count || 0}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Response Time Chart Component – safe with empty or partial data
const ResponseTimeChart = ({ data = {} }) => {
  const safeData = data && typeof data === 'object' ? data : {};
  const ranges = Object.keys(safeData);
  const values = ranges.map(k => Number(safeData[k]) || 0);
  const maxValue = values.length > 0 ? Math.max(...values, 1) : 1;
  return (
    <div className="space-y-4">
      {ranges.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No distribution data yet.</p>
      ) : (
        ranges.map((range, index) => {
          const val = Number(safeData[range]) || 0;
          const width = maxValue > 0 ? (val / maxValue) * 100 : 0;
          return (
            <div key={range}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{range}</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">{val} tickets</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-6 overflow-hidden">
                <div
                  className="bg-blue-500 dark:bg-blue-600 h-6 rounded-full transition-all duration-500 hover:bg-blue-600 dark:hover:bg-blue-500"
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// Resolution Time Chart Component – safe with empty or partial data
const ResolutionTimeChart = ({ data = {} }) => {
  const safeData = data && typeof data === 'object' ? data : {};
  const ranges = Object.keys(safeData);
  const values = ranges.map(k => Number(safeData[k]) || 0);
  const maxValue = values.length > 0 ? Math.max(...values, 1) : 1;

  return (
    <div className="space-y-4">
      {ranges.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No distribution data yet.</p>
      ) : (
        ranges.map((range) => {
          const val = Number(safeData[range]) || 0;
          const width = maxValue > 0 ? (val / maxValue) * 100 : 0;
          return (
            <div key={range}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{range}</span>
                <span className="text-sm text-gray-600 dark:text-gray-400">{val} tickets</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-6 overflow-hidden">
                <div
                  className="bg-green-500 dark:bg-green-600 h-6 rounded-full transition-all duration-500 hover:bg-green-600 dark:hover:bg-green-500"
                  style={{ width: `${width}%` }}
                />
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

// Agent Performance Table Component
const AgentPerformanceTable = ({ data = [] }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
        <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="text-base font-medium">No agent data available</p>
        <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">Data will appear when agents are assigned to tickets</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-slate-700">
      <table className="w-full min-w-[820px]">
        <thead className="bg-gray-50/50 dark:bg-slate-800/50 backdrop-blur-sm">
          <tr>
            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Agent</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Total Tickets</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Open</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">In Progress</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Resolved</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Closed</th>
            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Avg Resolution</th>
          </tr>
        </thead>
        <tbody>
          {data.map((agent, index) => (
            <tr key={index} className="border-b border-gray-100 dark:border-slate-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-900/10 transition-colors">
              <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100">{agent.name}</td>
              <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300 text-right font-medium">{agent.total_tickets || 0}</td>
              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">{agent.open_tickets || 0}</td>
              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">{agent.in_progress_tickets || 0}</td>
              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">{agent.resolved_tickets || 0}</td>
              <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400 text-right">{agent.closed_tickets || 0}</td>
              <td className="py-3 px-4 text-sm text-gray-700 dark:text-gray-300 text-right font-medium">
                {agent.avg_resolution_hours ? `${(agent.avg_resolution_hours / 24).toFixed(1)}d` : 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Reports;

